import { Caption, GroupTitle, Item, ListCard, Row } from './styles';
import Pill from '../shared/Pill';
import { channelLabel } from '../shared/labels';
import SectionState from '../shared/SectionState';

import { CopyListItem } from '@/services/lifecycleAPI';

const SPANISH_PILL: Record<
  string,
  { text: string; tone: 'amber' | 'green' } | undefined
> = {
  draft: { text: 'Spanish draft', tone: 'amber' },
  published: { text: 'Spanish', tone: 'green' },
};

const SpanishPill = ({ item }: { item: CopyListItem }) => {
  const pill = SPANISH_PILL[item.spanish_status];
  if (!pill) return null;
  return (
    <Pill tone={pill.tone} data-testid={`copyEsPill-${item.step_key}`}>
      {pill.text}
    </Pill>
  );
};

export const channelsText = (channels: string[]): string =>
  channels.map(channelLabel).join(', ');

const groupTitle = (item: CopyListItem): string => {
  if (item.transactional) return 'Transactional';
  return item.journey_name.startsWith(item.journey_key)
    ? item.journey_name
    : `${item.journey_key} · ${item.journey_name}`;
};

const groupItems = (items: CopyListItem[]) => {
  const groups: { title: string; items: CopyListItem[] }[] = [];
  items.forEach((item) => {
    const title = groupTitle(item);
    const group = groups.find((g) => g.title === title);
    if (group) group.items.push(item);
    else groups.push({ title, items: [item] });
  });
  return groups;
};

type Props = {
  items: CopyListItem[];
  loading: boolean;
  error: string | null;
  selected: string | null;
  onPick: (stepKey: string) => void;
  onRetry: () => void;
};

const CopyList = ({
  items,
  loading,
  error,
  selected,
  onPick,
  onRetry,
}: Props) => {
  const renderBody = () => {
    if (loading) return <SectionState mode="loading" title="messages" />;
    if (error) {
      return <SectionState mode="error" error={error} onRetry={onRetry} />;
    }
    if (items.length === 0) {
      return <SectionState mode="empty" title="No messages" />;
    }
    return groupItems(items).map((group) => (
      <div key={group.title}>
        <GroupTitle>{group.title}</GroupTitle>
        {group.items.map((item) => (
          <Item
            key={item.step_key}
            type="button"
            data-testid={`copyItem-${item.step_key}`}
            aria-current={item.step_key === selected ? 'true' : 'false'}
            onClick={() => onPick(item.step_key)}
          >
            <Row>
              <strong style={{ fontWeight: 600 }}>{item.label}</strong>
              {item.edited && <Pill tone="blue">Edited</Pill>}
              {item.has_draft && <Pill tone="amber">Draft</Pill>}
              <SpanishPill item={item} />
            </Row>
            <Caption>{`${item.step_key} · ${channelsText(item.channels)}`}</Caption>
          </Item>
        ))}
      </div>
    ));
  };

  return (
    <ListCard data-testid="copyList" aria-label="Messages">
      {renderBody()}
    </ListCard>
  );
};

export default CopyList;
